import { useEffect, useState, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { cloneDeep } from 'lodash'
import toast from 'react-hot-toast'
import { createSelector } from '@reduxjs/toolkit'
import { useDialog } from '@/ui-components/components/ConfirmDialog/useDialog'
import { useLimitDialog } from '@/ui-components/components/Modal/NumberLimitModal'
import { DEFAULT_CURRENCY } from '@/ui-components/utils/currency-config'
import { RootState } from '@/redux/reducers'
import {
  BetTypeEnum,
  TwoNumberPatternEnum,
} from '@/ui-components/components/BetLotto/BetLotto.types'
import { getRoundLottoConfig, responseLottoConfig } from '@/utils/lotto-type-v2'
import { getLimitTypeWithRateUsingTemplate } from '@/context/limitHelper'
import {
  type LottoItem,
  type LottoBetList,
  type BetPriceConfig,
  type AddLottoListResult,
  type HitData,
  type BetControlState,
  type BetControlActions,
} from '../types/bet-control.types'
import {
  generatePermutationsWithString,
  generateSwitchNumber,
  generateNineteenNumber,
} from '@/utils/number-utils'
import {
  AddLottoListHandler,
  AddLottoListOptions,
} from '@/ui-components/components/BetLotto/PanelSelection/PanelSelection.types'
import { NumberLimitModal } from '@/ui-components/components/Modal/NumberLimitModal'
import { BetGameItem, PayloadBetGame } from '@/redux/saga/fetchGameListSaga'
import { sagaActions } from '@/redux/sagaActions'
import { useNavigate } from 'react-router'
import { DISPLAY_BALANCE_ON_LOAD } from '@/constants/env'

// Base selectors for extracting data
const selectUserCredit = (state: RootState) => state.user.userBalance
const selectRound = (state: RootState) => state.games.round
const selectLimitTemplate = (state: RootState) => state.limitTemplate.limitRound
const selectResponse = (state: RootState) => state.games.response
const selectError = (state: RootState) => state.error.globalError
const selectProvider = (state: RootState) => state.user?.provider

// Combined selector that transforms the data
const betControlSelector = createSelector(
  [
    selectUserCredit,
    selectRound,
    selectLimitTemplate,
    selectResponse,
    selectError,
    selectProvider,
  ],
  (credit, round, limitTemplate, response, error, provider) => ({
    credit,
    round,
    limitTemplate, // Keep as undefined to match the expected type
    response,
    error,
    provider,
  }),
)

// Constants
const INIT_LOTTO_CONFIG = {
  betFour: {},
  betOne: {},
  betSix: {},
  betThree: {},
  betTwo: {},
}

const useBetControl = (): BetControlState & BetControlActions => {
  const { t } = useTranslation()
  const dialog = useDialog(t)
  const { showFromHitData, dialogState, handleConfirm, handleCancel } =
    useLimitDialog()
  const { credit, round, limitTemplate, response, error, provider } =
    useSelector(betControlSelector)
  const lastRoundIdRef = useRef<number | null>(null)
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const currencyObject = provider?.currency
  const betSetNumber = provider?.currency?.betSetPrice
  const dispatch = useDispatch()
  const navigation = useNavigate()

  const minRunningBet = provider?.currency?.minRunningBet ?? 30
  const BET_PRICE_CONFIG: BetPriceConfig = {
    bet_run_top: minRunningBet,
    bet_run_bottom: minRunningBet,
    bet_four: 0, // Will use winRate
    bet_six: 0, // Will use winRate
  }

  const calculateBetPrice = (
    type: BetTypeEnum,
    winRate: number,
    defaultPrice: number,
  ): number => {
    if (type === BetTypeEnum.FOUR || type === BetTypeEnum.SIX) {
      return winRate
    }
    return BET_PRICE_CONFIG[type] ?? defaultPrice
  }
  const [lottoConfig, setConfig] =
    useState<responseLottoConfig>(INIT_LOTTO_CONFIG)
  const [lottoBetList, setLottoBetList] = useState<LottoBetList[]>([])

  // On mount: fetch balance depending on feature flag behavior
  useEffect(() => {
    // If balance should NOT be displayed on load globally, always fetch when entering bet page
    if (!DISPLAY_BALANCE_ON_LOAD) {
      dispatch({ type: sagaActions.USER_FETCH_BALANCE })
      return
    }
    // Otherwise, fallback to fetching only if credit not yet present
    if (credit === undefined) {
      dispatch({ type: sagaActions.USER_FETCH_BALANCE })
    }
  }, [dispatch, credit])

  useEffect(() => {
    if (response && response.status === 'OK') {
      toast.success(t('message.successBet'))
      navigation(`/lotto/poll/${response.data.id}`)
      dispatch({ type: sagaActions.CLEAR_RESPONSE })
    }
    if (error?.message) {
      toast.error(error.message)
      dispatch({ type: sagaActions.CLEAR_GLOBAL_ERROR })
    }
  }, [response, dispatch, t, navigation, error])
  // Derived state
  const totalBetAmount = lottoBetList.reduce(
    (acc, curr) =>
      acc + curr.items.reduce((acc2, curr2) => acc2 + curr2.price, 0),
    0,
  )
  // If credit is undefined (balance not fetched/available), set as undefined to:
  // - allow betting (comparisons with undefined are true)
  // - enable UI to mask display as *****
  const creditLeft =
    credit === undefined ? undefined : (credit as number) - totalBetAmount

  // Helper functions
  const getUpdatePriceBySingleNumber = (
    type: BetTypeEnum,
    idx: number,
    newPrice: number,
  ) => {
    let price = newPrice
    if (
      [BetTypeEnum.RUN_BOTTOM, BetTypeEnum.RUN_TOP].includes(type) &&
      price < minRunningBet
    ) {
      price = minRunningBet
    }
    const betListItems = lottoBetList.find((v) => v.type === type)
    return (
      betListItems?.items.reduce(
        (acc, item, k) => acc + (k === idx ? price : item.price),
        0,
      ) ?? 0
    )
  }

  const calCulateNewUpdatePrice = useCallback(
    (newPrice: number) => {
      const totalUpdatedPrice = lottoBetList.reduce(
        (acc, item) => acc + item.items.length * newPrice,
        0,
      )
      return isNaN(totalUpdatedPrice) ? 0 : totalUpdatedPrice
    },
    [lottoBetList],
  )

  const findRateByType = useCallback(
    (type: BetTypeEnum): number => {
      for (const configKey of Object.keys(lottoConfig)) {
        const config = lottoConfig[configKey as keyof typeof lottoConfig]
        const rate = config[type]
        if (rate !== undefined) {
          return rate
        }
      }
      return 0
    },
    [lottoConfig],
  )

  // Limit checking
  const limitCheck = useCallback(
    (betList: LottoBetList[]) => {
      const copy = cloneDeep(betList)
      const hitData: HitData = {
        data: [],
        type: 'limit',
      }

      copy.forEach((group) => {
        group.items = group.items.filter((item) => {
          const originRate = findRateByType(group.type as BetTypeEnum)
          const { limit, appliedRate } = getLimitTypeWithRateUsingTemplate(
            item.number,
            group.type,
            originRate,
            item.price,
            limitTemplate,
          )

          if (limit === 'range') {
            item.rate = appliedRate
            item.winPrice = appliedRate * item.price

            hitData.data.push({
              type: t(`betDetailPanel.betLottoType.${group.type}`),
              number: item.number,
              amount: parseFloat(appliedRate?.toFixed(2) || '0'),
            })
            return true
          } else if (limit === 'limit') {
            hitData.data.push({
              type: t(`betDetailPanel.betLottoType.${group.type}`),
              number: item.number,
              amount: 0,
            })

            return false
          }
          return true
        })
      })

      setLottoBetList(copy)
      if (hitData.data.length > 0) {
        showFromHitData(hitData)
      }
    },
    [limitTemplate, t, showFromHitData, findRateByType],
  )

  // Debounced onChangeBetPrice function
  const debouncedOnChangeBetPrice = useCallback(
    (newPrice: number) => {
      // Clear existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }

      // Set new timeout
      debounceTimeoutRef.current = setTimeout(() => {
        const updateNewPrice = calCulateNewUpdatePrice(newPrice)
        if (!newPrice) {
          // Should we have another message for this case?
          toast.error(t('message.creditNotEnough'))
          return
        }
        // Only enforce balance check when credit is known
        if (credit !== undefined && updateNewPrice > credit) {
          toast.error(t('message.creditNotEnough'))
          return
        }

        const updatedLottoBetList = lottoBetList.map((item) => ({
          ...item,
          items: item.items.map((v) => {
            let price = newPrice
            if (
              [BetTypeEnum.RUN_BOTTOM, BetTypeEnum.RUN_TOP].includes(
                item.type,
              ) &&
              price < minRunningBet
            ) {
              price = minRunningBet
            }
            return {
              ...v,
              price,
              winPrice: v.rate * price,
            }
          }),
        }))

        limitCheck(updatedLottoBetList)
      }, 300) // 300ms debounce delay
    },
    [
      credit,
      lottoBetList,
      t,
      calCulateNewUpdatePrice,
      limitCheck,
      minRunningBet,
    ],
  )

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [])

  // Initialization
  const setDefaultLottoBetList = useCallback((config: responseLottoConfig) => {
    const defaultItem: LottoBetList[] = []
    if (!config) return

    Object.values(config).forEach((val) => {
      Object.entries(val).forEach(([key, rate]) => {
        const type = key as BetTypeEnum
        defaultItem.push({
          items: [],
          name: `betDetailPanel.betLottoType.${type}`,
          type,
          rate,
        })
      })
    })
    setLottoBetList(defaultItem)
  }, [])

  useEffect(() => {
    if (round && round.id !== lastRoundIdRef.current) {
      const newConfig = getRoundLottoConfig(round)
      setConfig(newConfig)
      setDefaultLottoBetList(newConfig)
      lastRoundIdRef.current = round.id
    }
  }, [round, setConfig, setDefaultLottoBetList])

  // Bet price management
  const onChangeSingleBetPrice = (
    type: BetTypeEnum,
    number: string,
    amount: number,
  ) => {
    let price = amount && !isNaN(amount) ? amount : 1
    if (
      [BetTypeEnum.RUN_BOTTOM, BetTypeEnum.RUN_TOP].includes(type) &&
      price < minRunningBet
    ) {
      price = minRunningBet
    }
    const group = lottoBetList.find((v) => v.type === type)
    const idx = group?.items.findIndex((item) => item.number === number) ?? -1
    if (idx === -1) return

    const updatePrice = getUpdatePriceBySingleNumber(type, idx, price)
    // Only enforce balance check when credit is known
    if (credit !== undefined && updatePrice > credit) {
      toast.error(t('message.creditNotEnough'))
      return
    }

    const hitData: HitData = {
      data: [],
      type: 'limit',
    }

    const newListBet = lottoBetList.map((v) => {
      if (v.type !== type || !v.items?.[idx]) return v

      const originRate = findRateByType(type)
      const { limit, appliedRate } = getLimitTypeWithRateUsingTemplate(
        number,
        type,
        originRate,
        price,
        limitTemplate,
      )

      const updatedItem = {
        ...v.items[idx],
        price,
        winPrice: appliedRate * price,
        rate: appliedRate,
      }

      if (limit === 'range' && v.items[idx].rate !== appliedRate) {
        hitData.data.push({
          type: t(`betDetailPanel.betLottoType.${type}`),
          number: number,
          amount: parseFloat(appliedRate?.toFixed(2) || '0'),
        })
        v.items[idx] = updatedItem
      } else if (limit === 'limit') {
        hitData.data.push({
          type: t(`betDetailPanel.betLottoType.${type}`),
          number: number,
          amount: 0,
        })
        v.items = v.items.filter((i) => i.number !== number)
      } else {
        v.items[idx] = updatedItem
      }

      return v
    })

    setLottoBetList(newListBet)
    toast.success(t('message.updatePriceSuccess'))

    if (hitData.data.length > 0) {
      showFromHitData(hitData)
    }
  }

  const onChangeBetPrice = (newPrice: number) => {
    debouncedOnChangeBetPrice(newPrice)
  }

  // Item management
  const onDeleteItem = async (
    type: BetTypeEnum,
    number: string,
    idx: number,
  ) => {
    await dialog.confirm(t('message.confirmDeleteNumber', { number }), {
      onConfirm: async () => {
        const newList = lottoBetList.map((v) => {
          if (v.type !== type) return v
          return {
            ...v,
            items: v.items.filter((_, i) => i !== idx),
          }
        })
        setLottoBetList(newList)
        toast.success(t('message.deleteItemSuccess', { number }))
      },
    })
  }

  const onDeleteByNumberAndType = async (
    number: string,
    types: BetTypeEnum[],
  ) => {
    await dialog.confirm(t('message.confirmDeleteNumber', { number }), {
      onConfirm: async () => {
        let hasDeleted = false
        const newList = lottoBetList.map((v) => {
          if (!types.includes(v.type as BetTypeEnum)) return v
          const filteredItems = v.items.filter((item) => item.number !== number)
          if (filteredItems.length !== v.items.length) {
            hasDeleted = true
          }
          return {
            ...v,
            items: filteredItems,
          }
        })
        setLottoBetList(newList)
        if (hasDeleted) {
          toast.success(t('message.deleteItemSuccess', { number }))
        }
      },
    })
  }

  const onClearAll = async () => {
    await dialog.confirm(t('message.confirmClearAll'), {
      onConfirm: () => setDefaultLottoBetList(lottoConfig),
    })
  }

  // Item management
  const addLottoListByType = (
    number: string,
    types: BetTypeEnum[],
    betPrice = 1,
  ): AddLottoListResult => {
    // Early validation
    // If credit is undefined, allow adding items
    if (creditLeft !== undefined && creditLeft <= 0) {
      toast.error(t('message.creditNotEnough'))
      return {
        data: [],
        type: 'limit',
      }
    }

    const updatedBetList = [...lottoBetList]
    const hitData: HitData = {
      data: [],
      type: 'limit',
    }

    // Process each bet type
    types.forEach((type) => {
      const betType = type as BetTypeEnum
      const group = updatedBetList.find((item) => item.type === betType)
      if (!group) return

      const originRate = findRateByType(betType)
      const pBetPrice = calculateBetPrice(betType, originRate, betPrice)

      const { limit, appliedRate } = getLimitTypeWithRateUsingTemplate(
        number,
        betType,
        originRate,
        betPrice,
        limitTemplate,
      )

      const newBetItem: LottoItem = {
        price: pBetPrice,
        number,
        winPrice: appliedRate * pBetPrice,
        rate: appliedRate,
      }

      const existingBetIndex = group.items.findIndex(
        (item) => item.number === number,
      )

      // Handle limit cases
      if (limit === 'limit') {
        hitData.data.push({
          type: t(`betDetailPanel.betLottoType.${betType}`),
          number: number,
          amount: 0,
        })
        return
      }

      // Handle range and none cases
      if (limit === 'range' || limit === 'none') {
        if (existingBetIndex === -1 && newBetItem.winPrice > 0) {
          group.items.push(newBetItem)

          if (limit === 'range') {
            hitData.data.push({
              type: t(`betDetailPanel.betLottoType.${betType}`),
              number: number,
              amount: parseFloat(appliedRate?.toFixed(2) || '0'),
            })
          }

          toast.success(t('message.successBetNumber', { number }))
        }
      }
    })

    // Update state and return results
    setLottoBetList(updatedBetList)
    return hitData
  }

  const handleRotatedNumber = (
    number: string,
    betTypes: BetTypeEnum[],
    betPrice: number,
  ) => {
    const permutations = generatePermutationsWithString(number)
    let resp: AddLottoListResult | undefined
    permutations.forEach((result) => {
      resp = addLottoListByType(result, betTypes, betPrice)
    })
    return resp
  }

  const handleFrontRunning = (
    number: string,
    betTypes: BetTypeEnum[],
    betPrice: number,
  ) => {
    const results = generateSwitchNumber(parseInt(number))
    let resp: AddLottoListResult | undefined
    results.forEach((result) => {
      resp = addLottoListByType(result, betTypes, betPrice)
    })
    return resp
  }

  const handleEndRunning = (
    number: string,
    betTypes: BetTypeEnum[],
    betPrice: number,
  ) => {
    const results = generateSwitchNumber(parseInt(number), true)
    let resp: AddLottoListResult | undefined
    results.forEach((result) => {
      resp = addLottoListByType(result, betTypes, betPrice)
    })
    return resp
  }

  const handleNineteenNumber = (
    number: string,
    betTypes: BetTypeEnum[],
    betPrice: number,
  ) => {
    const results = generateNineteenNumber(parseInt(number))
    let resp: AddLottoListResult | undefined
    results.forEach((result) => {
      resp = addLottoListByType(result, betTypes, betPrice)
    })
    return resp
  }

  const handleTwoNumberPattern = (
    number: string,
    betTypes: BetTypeEnum[],
    betPrice: number,
    pattern: TwoNumberPatternEnum,
  ) => {
    switch (pattern) {
      case TwoNumberPatternEnum.FRONT_RUNNING:
        return handleFrontRunning(number, betTypes, betPrice)
      case TwoNumberPatternEnum.END_RUNNING:
        return handleEndRunning(number, betTypes, betPrice)
      case TwoNumberPatternEnum.NINETEEN_NUMBER:
        return handleNineteenNumber(number, betTypes, betPrice)
      default:
        return undefined
    }
  }

  const handleAddLottoListResponse = (resp: AddLottoListResult) => {
    if (resp.data.length > 0) {
      showFromHitData(resp)
    }
  }

  const addLottoListAndHandleResponse: AddLottoListHandler = (
    number: string,
    betTypes: BetTypeEnum[],
    betPrice = 1,
    options: AddLottoListOptions = {},
  ): void => {
    if (betTypes.length === 0) {
      toast.error(t('error.ERROR_BET_TYPE_NOT_FOUND'))
      return
    }
    // If credit is undefined, allow betting
    if (creditLeft !== undefined && creditLeft <= 0) {
      toast.error(t('betDetailPanel.creditLeft'))
      return
    }

    let resp: AddLottoListResult | undefined

    if (options.rotated) {
      resp = handleRotatedNumber(number, betTypes, betPrice)
    } else if (options.twoNumberPattern) {
      resp = handleTwoNumberPattern(
        number,
        betTypes,
        betPrice,
        options.twoNumberPattern,
      )
    } else {
      resp = addLottoListByType(number, betTypes, betPrice)
    }

    if (resp) {
      handleAddLottoListResponse(resp)
    }
  }
  const handleBetSubmit = () => {
    const betGameItem: BetGameItem[] = []
    lottoBetList
      .filter((i) => i.items.length > 0)
      .map((i) =>
        i.items?.map((b) => {
          betGameItem.push({
            type: i.type,
            number: b.number,
            price: b.price,
            rate: b.rate,
          })
        }),
      )

    if (betGameItem.length === 0) {
      dialog.confirm(t('message.noBetItem'), {})
      return
    }
    if (betGameItem.length > 99) {
      dialog.confirm(t('message.maxBetItem'), {})
      return
    }
    const roundId = round?.id
    if (roundId) {
      const payload: PayloadBetGame = {
        roundId: roundId,
        betList: betGameItem,
      }
      dialog.confirm(t('message.confirmBet'), {
        onConfirm: () => {
          dispatch({ type: sagaActions.POST_BET_GAME, payload })
        },
      })
    }
  }
  return {
    lottoBetList,
    creditLeft,
    totalBetAmount,
    lottoConfig,
    onChangeSingleBetPrice,
    onDeleteItem,
    onDeleteByNumberAndType,
    onClearAll,
    onChangeBetPrice,
    addLottoListAndHandleResponse,
    handleBetSubmit,
    currencyObject: currencyObject || DEFAULT_CURRENCY,
    betSetNumber: betSetNumber || [10, 20, 50, 100],
    dialogComponent: dialog.render(),
    limitDialogComponent: (
      <NumberLimitModal
        isOpen={dialogState.isOpen}
        onClose={() => {}}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        data={dialogState.data}
        type={dialogState.type}
      />
    ),
  }
}

export default useBetControl
